// A sheet about another guild (guild/details): crest, name, tag, motto, rank / members / points, its diplomacy lists,
// and the one action that fits — designate it (in a guild, Warlord/General) or ask to join it (no guild).
import { useState } from "react";
import { api } from "@/api/client";
import { useWorldCity } from "@/app/WorldPage";
import { art } from "@/kit/art";
import { Sheet } from "@/kit/overlays";
import { Button, Empty, Medallion, Seg, StatBand } from "@/kit/primitives";
import { useGuildDetails } from "@/state/mapQueries";
import { fmtNum, fmtRank } from "@/game/format";
import { DIPLOMACY_KINDS, can, guildDetail, type DiplomacyType } from "@/game/guild";

export function GuildPeek({ wid, vid, target, onClose, myGid, level, requested }: {
  wid: number; vid: number | null; target: { gid: number; tag?: string } | null; onClose: () => void;
  /** your guild's id when you are in one (enables Designate) */
  myGid?: number; level?: number;
  /** no-guild: the tags you have already asked */
  requested?: Set<number>;
}) {
  const { actions } = useWorldCity();
  const q = useGuildDetails(wid, vid ?? 0, target?.gid ?? null);
  const g = q.data?.guild_info;
  const [type, setType] = useState<DiplomacyType>("a");
  const inGuild = !!myGid && myGid > 0;
  const own = inGuild && myGid === target?.gid;
  const name = g?.name ?? target?.tag ?? "Guild";
  return (
    <Sheet open={!!target} onClose={onClose} label={name}>
      {q.isLoading && <Empty><span className="spin" /> Reading…</Empty>}
      {q.isError && <Empty>Couldn't read this guild.</Empty>}
      {g && (
        <div className="stack" style={{ gap: 14 }}>
          <div className="gd-herorow">
            <Medallion src={art.social.crest} alt="" size={84} glow />
            <div className="gd-herotxt">
              <h2 style={{ fontSize: "calc(var(--fs-h) * 1.3)" }}>{g.name}</h2>
              <div className="sub"><b>[{g.tag}]</b>{g.description ? <> · <i>“{g.description}”</i></> : null}</div>
            </div>
          </div>
          <StatBand stats={[
            { label: "Rank", value: fmtRank(guildDetail(g, "Rank")) },
            { label: "Members", value: fmtNum(guildDetail(g, "Members") ?? g.members?.length ?? 0) },
            { label: "Points", value: fmtNum(guildDetail(g, "Total Points") ?? 0) },
          ]} />
          {(g.diplomacy?.allies?.length || g.diplomacy?.nap?.length || g.diplomacy?.enemies?.length) ? (
            <p className="c-note left" style={{ margin: 0 }}>
              {g.diplomacy.allies?.length ? <>Allies: <b className="gold">{g.diplomacy.allies.map((d) => `[${d.gtag}]`).join(" ")}</b>. </> : null}
              {g.diplomacy.nap?.length ? <>Pacts: <b className="gold">{g.diplomacy.nap.map((d) => `[${d.gtag}]`).join(" ")}</b>. </> : null}
              {g.diplomacy.enemies?.length ? <>Enemies: <b className="crimtxt">{g.diplomacy.enemies.map((d) => `[${d.gtag}]`).join(" ")}</b>.</> : null}
            </p>
          ) : null}
          {inGuild && !own && level !== undefined && can.diplomacy(level) && (
            <>
              <Seg value={type} onChange={setType} options={DIPLOMACY_KINDS.map((k) => ({ value: k.type, label: k.one }))} />
              <Button kind="crimson" size="md" wide busy={actions.isBusy("peek-designate")} onClick={() => void actions.run("peek-designate", () => api.guild.addDiplomacy({ wid, vid: vid! }, myGid!, g.tag, type), { success: `[${g.tag}] marked as ${DIPLOMACY_KINDS.find((k) => k.type === type)!.one.toLowerCase()}.` }).then(onClose)}>
                Designate [{g.tag}]
              </Button>
            </>
          )}
          {!inGuild && (
            <Button kind="crimson" size="md" wide disabled={requested?.has(g.id)} busy={actions.isBusy("peek-request")} onClick={() => void actions.run("peek-request", () => api.guild.addRequest({ wid, vid: vid! }, g.id, g.tag), { success: `You asked to join ${g.name}.` }).then(onClose)}>
              {requested?.has(g.id) ? "Request sent" : `Ask to join [${g.tag}]`}
            </Button>
          )}
        </div>
      )}
    </Sheet>
  );
}
